import {
  ButtonKind,
  CircleDrawIcon,
  EditBoundaryIcon,
  FreehandIcon,
  Icon,
  MeatballMenu,
  MeatballMenuItem,
  OptionButton,
  PanoramaButton,
  PolygonIcon,
  RectangleIcon,
  SplitIcon,
  UndoIcon
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import {
  DrawMode,
  DrawStyle,
  useCurrentZones,
  useDrawMode,
  useDrawShape,
  useMapContext,
  useSelectedId
} from 'shared-ui/map'
import { isNonNil } from 'utils'

import type { ZoneGuidance } from './formtypes'
import { type LineRow, PRIMITIVE_ICONS } from './LineList'
import type { Draft } from './wizardStep'

const SHAPE_OPTIONS = [
  { type: DrawStyle.Freehand, Icon: FreehandIcon },
  { type: DrawStyle.Polygon, Icon: PolygonIcon },
  { type: DrawStyle.Circle, Icon: CircleDrawIcon },
  { type: DrawStyle.Rectangle, Icon: RectangleIcon }
] as const

const TapIcon = ({ className }: { className: string }) => (
  <Icon icon={SpriteMap.ClickIcon} className={className} />
)

// The focused panel shown while a carve draft is open: tap, cut and shape tools plus
// Confirm/Cancel. The draft's zone and line already live in the draw tool, so
// everything here only steers interactions and ends the transaction.
const DraftPanel = ({
  lineName,
  areaAc,
  onConfirm,
  onCancel,
  onUndo
}: {
  lineName: string
  areaAc: number
  onConfirm: () => void
  onCancel: () => void
  onUndo: () => void
}) => {
  const { t, formatMetric } = useLocalization()
  const { map } = useMapContext()
  const drawMode = useDrawMode()
  const drawShape = useDrawShape()

  const picking = drawMode === DrawMode.AssignZone
  const shapesDisabled = drawMode === DrawMode.Split || picking

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="pan-title4">{t(($) => $.planning.carve.draftHeader, { name: lineName })}</div>
      <p className="text-sm text-gray-500">
        {picking
          ? t(($) => $.planning.carve.cutHint)
          : t(($) => $.planning.carve.draftInstructions)}
      </p>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          {formatMetric({ value: areaAc, unit: 'ac' })}
        </span>
        <PanoramaButton
          type="button"
          kind={ButtonKind.Secondary}
          className="h-fit py-1"
          onClick={onUndo}
        >
          {t(($) => $.buttons.undo)}
          <UndoIcon />
        </PanoramaButton>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <OptionButton
          title={t(($) => $.planning.carve.tapToAdd)}
          description={t(($) => $.planning.carve.tapToAddDescription)}
          active={picking}
          IconElement={TapIcon}
          onClick={() =>
            map.drawTool.setDrawStyle({ mode: DrawMode.AssignZone, shape: DrawStyle.Polygon })
          }
        />
        <OptionButton
          title={t(($) => $.planning.carve.cut)}
          description={t(($) => $.planning.carve.cutDescription)}
          active={drawMode === DrawMode.Split}
          IconElement={SplitIcon}
          onClick={() => map.drawTool.setDrawStyle({ mode: DrawMode.Split, shape: DrawStyle.Line })}
        />
        <OptionButton
          title={t(($) => $.planning.carve.addShape)}
          description={t(($) => $.planning.carve.addShapeDescription)}
          active={drawMode === DrawMode.AddZone}
          IconElement={EditBoundaryIcon}
          onClick={() =>
            map.drawTool.setDrawStyle({
              mode: DrawMode.AddZone,
              shape: drawShape === DrawStyle.Line ? DrawStyle.Polygon : drawShape
            })
          }
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {SHAPE_OPTIONS.map(({ type, Icon: ShapeIcon }) => (
          <OptionButton
            key={type}
            disabled={shapesDisabled}
            onClick={() => map.drawTool.setDrawStyle({ mode: DrawMode.AddZone, shape: type })}
            title={t(($) => $.planning.boundaryCreation.shapes[type])}
            active={!shapesDisabled && drawShape === type}
            IconElement={ShapeIcon}
          />
        ))}
      </div>
      <div className="flex gap-3 border-t border-gray-200 pt-4">
        <PanoramaButton
          type="button"
          kind={ButtonKind.Secondary}
          icon={SpriteMap.CloseCircleIcon}
          className="w-full"
          onClick={onCancel}
        >
          {t(($) => $.buttons.cancel)}
        </PanoramaButton>
        <PanoramaButton
          type="button"
          kind={ButtonKind.Primary}
          icon={SpriteMap.CheckCircleIcon}
          className="w-full"
          disabledWhen={{
            when: areaAc === 0,
            reason: t(($) => $.disabledReasons.fillInRequiredFields)
          }}
          onClick={onConfirm}
        >
          {t(($) => $.planning.carve.confirm)}
        </PanoramaButton>
      </div>
    </div>
  )
}

export const Carve = ({
  areas,
  trayLines,
  zoneGuidance,
  lineNameById,
  remainderZoneId,
  draft,
  onCarve,
  onEditCarve,
  onConfirmCarve,
  onCancelCarve,
  onUndoCarve,
  onUnCarve,
  onRemoveFromPlan,
  onPick
}: {
  areas: { area: number; zones: Record<string, number> }
  trayLines: LineRow[]
  zoneGuidance: Record<string, ZoneGuidance>
  // Every known saved line's display name, keyed by asset id — placed lines
  // included, so region rows can label an assignment that's no longer in the
  // tray.
  lineNameById: Record<string, string>
  remainderZoneId: string
  draft: Draft | null
  onCarve: (line: LineRow) => void
  onEditCarve: (zoneId: string) => void
  onConfirmCarve: () => void
  onCancelCarve: () => void
  onUndoCarve: () => void
  onUnCarve: (zoneId: string) => void
  onRemoveFromPlan: (lineId: string) => void
  onPick: (zoneId: string, guidance: ZoneGuidance) => void
}) => {
  const { t, formatMetric } = useLocalization()
  const { map } = useMapContext()
  const currentZones = useCurrentZones()
  const selectedId = useSelectedId()

  const zones = currentZones ? Array.from(currentZones.zones.values()) : []

  if (draft !== null) {
    return (
      <DraftPanel
        lineName={lineNameById[draft.lineId] ?? ''}
        areaAc={areas.zones[draft.zoneId] ?? 0}
        onConfirm={onConfirmCarve}
        onCancel={onCancelCarve}
        onUndo={onUndoCarve}
      />
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4">
      <p className="text-sm text-gray-500">{t(($) => $.planning.carve.instructions)}</p>

      <div className="flex items-center justify-between">
        <div className="pan-title4">{t(($) => $.planning.carve.trayHeader)}</div>
        {trayLines.length > 0 ? (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
            {t(($) => $.planning.carve.unplaced, { count: trayLines.length })}
          </span>
        ) : (
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            {t(($) => $.planning.carve.allPlaced)}
          </span>
        )}
      </div>

      {trayLines.length > 0 && (
        <div className="flex flex-col gap-2">
          {trayLines.map((line) => {
            const isSelected = selectedId === line.id
            return (
              <div
                key={line.id}
                className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${
                  isSelected ? 'border-gray-800' : 'border-gray-200'
                }`}
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  onClick={() => map.drawTool.setSelected(isSelected ? null : line.id)}
                >
                  <Icon
                    icon={PRIMITIVE_ICONS[line.type]}
                    className="size-4 shrink-0 text-gray-500"
                  />
                  <span className="truncate text-sm font-medium text-gray-800">{line.name}</span>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  <PanoramaButton
                    kind={ButtonKind.Secondary}
                    className="h-fit py-1"
                    onClick={() => onCarve(line)}
                  >
                    {t(($) => $.planning.carve.carve)}
                  </PanoramaButton>
                  <MeatballMenu>
                    <MeatballMenuItem
                      color="danger"
                      icon={<Icon icon={SpriteMap.TrashIcon} className="size-5" />}
                      title={t(($) => $.planning.lineList.removeFromPlan)}
                      onClick={() => onRemoveFromPlan(line.id)}
                    />
                  </MeatballMenu>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="pan-title4">{t(($) => $.planning.carve.regionsHeader)}</div>
      <div className="flex flex-col gap-2">
        {zones.map((zone) => {
          const isRemainder = zone.id === remainderZoneId
          const isActiveZone = selectedId === zone.id
          const guidance = zoneGuidance[zone.id] as ZoneGuidance | undefined
          const areaAc = areas.zones[zone.id]
          // The picker offers every line still in the tray, with the current
          // assignment appended so the select can display it.
          const currentSavedName = guidance !== undefined ? lineNameById[guidance.id] : undefined
          const lineOptions = [
            ...trayLines.map((l) => ({ id: l.id, name: l.name })),
            ...(guidance !== undefined &&
            isNonNil(currentSavedName) &&
            !trayLines.some((l) => l.id === guidance.id)
              ? [{ id: guidance.id, name: currentSavedName }]
              : [])
          ]
          return (
            <div key={zone.id} className="rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => map.drawTool.setSelected(zone.id)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-3 shrink-0 rounded-full"
                    style={{ backgroundColor: zone.color }}
                  />
                  <span className="truncate text-sm font-medium text-gray-800">
                    {isRemainder ? t(($) => $.planning.carve.remainder) : zone.name}
                  </span>
                  {isNonNil(areaAc) && (
                    <span className="shrink-0 text-xs text-gray-400">
                      {formatMetric({ value: areaAc, unit: 'ac' })}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Icon
                    icon={SpriteMap.ChevronIcon}
                    className={`size-3 text-gray-500 transition-transform ${isActiveZone ? '' : 'rotate-180'}`}
                  />
                </div>
              </button>

              <div
                className={`flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-4 py-4 ${isActiveZone ? '' : 'hidden'}`}
              >
                {guidance === undefined && (
                  <p className="text-xs text-gray-500">{t(($) => $.planning.carve.noLine)}</p>
                )}
                <div className="relative">
                  <select
                    value={guidance?.id ?? ''}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value) onPick(zone.id, { id: value })
                    }}
                    className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-7 text-sm outline-green-500"
                  >
                    {guidance === undefined && <option value="" />}
                    {lineOptions.length > 0 && (
                      <optgroup label={t(($) => $.planning.savedLines)}>
                        {lineOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <Icon
                    icon={SpriteMap.ChevronIcon}
                    className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 rotate-180 text-gray-500"
                  />
                </div>
                {!isRemainder && (
                  <div className="flex gap-2">
                    <PanoramaButton
                      kind={ButtonKind.Secondary}
                      icon={SpriteMap.EditIcon}
                      className="h-fit py-1"
                      onClick={() => onEditCarve(zone.id)}
                    >
                      {t(($) => $.planning.carve.edit)}
                    </PanoramaButton>
                    <PanoramaButton
                      kind={ButtonKind.Secondary}
                      className="h-fit py-1"
                      onClick={() => onUnCarve(zone.id)}
                    >
                      {t(($) => $.planning.carve.returnToTray)}
                    </PanoramaButton>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
